import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Sparkles, Clock, BadgeEuro } from "lucide-react";
import { COMPANY } from "@/data";
import { Reveal } from "@/components/motion";

const OWNERS = [
  { name: "Mahmoud Alhaj Abdo", role: "Inhaber", image: "/team/mahmoud.jpg" },
  { name: "Timon Malms", role: "Inhaber", image: "/team/timon.jpg" },
];

const REASONS = [
  { icon: ShieldCheck, title: "Zuverlässig", text: "Termintreue und verlässliche Kommunikation – auf uns können Sie bauen." },
  { icon: Sparkles, title: "Gründlich", text: "Wir arbeiten sauber und mit Liebe zum Detail. Kein Auftrag ist uns zu klein." },
  { icon: BadgeEuro, title: "Faire Preise", text: "Transparente Angebote ohne versteckte Kosten. Sie wissen immer, woran Sie sind." },
  { icon: Clock, title: "Schnell vor Ort", text: "In Gelnhausen und 50 km Umkreis kurzfristig und flexibel für Sie im Einsatz." },
];

export default function About() {
  return (
    <main data-testid="about-page">
      <section className="relative bg-ink text-white grain pt-40 pb-20 md:pt-48 md:pb-28">
        <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <span className="overline text-xs uppercase tracking-[0.3em] text-gold font-medium">Über uns</span>
            <h1 className="mt-4 font-serif text-5xl md:text-7xl tracking-tight leading-none max-w-4xl">
              Zwei Namen, ein <span className="text-gold-gradient italic">Versprechen.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-white/55 font-light">
              M&amp;A Objektservice GbR wurde von Mahmoud Alhaj Abdo und Timon Malms gegründet – mit dem Anspruch,
              Objektpflege wieder persönlich, verlässlich und auf höchstem Niveau anzubieten.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            {OWNERS.map((o, i) => (
              <Reveal key={o.name} delay={i * 0.1}>
                <div className="group hover-lift rounded-3xl border border-black/5 bg-white overflow-hidden">
                  <div className="relative aspect-[4/5] bg-neutral-100 overflow-hidden">
                    <img
                      src={o.image}
                      alt={o.name}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-5 left-6 right-6 text-white">
                      <h3 className="font-serif text-2xl">{o.name}</h3>
                      <p className="text-xs text-gold uppercase tracking-[0.2em] mt-1">{o.role}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-20 grid md:grid-cols-12 gap-10 items-start">
            <Reveal className="md:col-span-4">
              <h2 className="font-serif text-3xl md:text-4xl text-ink tracking-tight">Unsere Werte</h2>
            </Reveal>
            <Reveal delay={0.1} className="md:col-span-8">
              <p className="text-lg text-neutral-600 font-light leading-relaxed">
                Wir behandeln jedes Objekt so, als wäre es unser eigenes. Das bedeutet: sorgfältige Arbeit,
                ehrliche Beratung und faire Preise. Ob wöchentliche Betreuung oder einmaliger Auftrag –
                unser Ziel ist immer dasselbe: ein Ergebnis, das Sie weiterempfehlen.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ink text-white grain py-24 md:py-32">
        <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <span className="overline text-xs uppercase tracking-[0.2em] text-gold font-medium">Warum M&amp;A Objektservice?</span>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight max-w-2xl">Vier gute Gründe für uns.</h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {REASONS.map((r, i) => (
              <Reveal key={r.title} delay={(i % 4) * 0.08}>
                <div className="h-full rounded-2xl border border-white/10 bg-surface p-7">
                  <r.icon className="text-gold mb-5" size={28} />
                  <h3 className="font-serif text-xl">{r.title}</h3>
                  <p className="mt-2 text-sm text-white/55 leading-relaxed">{r.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-16 text-center">
              <Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-ink font-medium hover:bg-gold-light transition-all hover:shadow-[0_0_30px_rgba(201,169,97,0.4)]">
                Kostenlose Besichtigung anfragen <ArrowUpRight size={18} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
